// src/modules/competition/components/CompetitorsTable/index.js
import React, { useState, useMemo } from 'react';
import { Table } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CompetitorsTableBody from './CompetitorsTableBody';
import TeamCompetitorsTableBody from './TeamCompetitorsTableBody';
import './styles.css';

const SortIcon = ({ field, sortBy, sortDir }) => {
    if (sortBy !== field) return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.75rem' }} />;
    return sortDir === 'asc'
        ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.75rem' }} />
        : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.75rem' }} />;
};

const CompetitorsTable = ({ competitors, lowestCompetitor, pointTarget, onUsernameClick, isTeamBased, teams }) => {
    const [sortBy, setSortBy] = useState('rank');
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir(field === 'name' || field === 'rank' ? 'asc' : 'desc');
        }
    };

    const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

    // Assign stable competition ranks (by points desc) before any user-driven sort
    const rankedCompetitors = useMemo(() => {
        const byPoints = [...competitors].sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0));
        const rankMap = Object.fromEntries(byPoints.map((c, i) => [c.username, i + 1]));
        return competitors.map(c => ({ ...c, rank: rankMap[c.username] }));
    }, [competitors]);

    const rankedTeams = useMemo(() => {
        if (!teams) return teams;
        const byPoints = [...teams].sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0));
        const rankMap = Object.fromEntries(byPoints.map((t, i) => [t.teamId, i + 1]));
        return teams.map(t => ({ ...t, rank: rankMap[t.teamId] }));
    }, [teams]);

    const sortedCompetitors = useMemo(() => {
        return [...rankedCompetitors].sort((a, b) => {
            let cmp = 0;
            if (sortBy === 'name') {
                cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
            } else if (sortBy === 'activities') {
                cmp = (a.numActivities ?? 0) - (b.numActivities ?? 0);
            } else if (sortBy === 'rank') {
                cmp = (a.rank ?? 0) - (b.rank ?? 0);
            } else {
                cmp = (a.totalPoints ?? 0) - (b.totalPoints ?? 0);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rankedCompetitors, sortBy, sortDir]);

    const sortedTeams = useMemo(() => {
        if (!rankedTeams) return rankedTeams;
        return [...rankedTeams].sort((a, b) => {
            let cmp = 0;
            if (sortBy === 'name') {
                cmp = a.name.localeCompare(b.name);
            } else if (sortBy === 'activities') {
                const competitorsByUsername = Object.fromEntries(competitors.map(c => [c.username, c]));
                const actsA = (a.members || []).reduce((s, m) => s + (competitorsByUsername[m]?.numActivities ?? 0), 0);
                const actsB = (b.members || []).reduce((s, m) => s + (competitorsByUsername[m]?.numActivities ?? 0), 0);
                cmp = actsA - actsB;
            } else if (sortBy === 'rank') {
                cmp = (a.rank ?? 0) - (b.rank ?? 0);
            } else {
                cmp = (a.totalPoints ?? 0) - (b.totalPoints ?? 0);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rankedTeams, competitors, sortBy, sortDir]);

    return (
        <div className="competitors-table-wrapper">
            <Table striped hover responsive>
                <thead>
                    <tr>
                        <th style={{ width: '2%', ...thStyle }} onClick={() => handleSort('rank')}>
                            #<SortIcon field="rank" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th style={{ width: '28%', ...thStyle }} onClick={() => handleSort('name')}>
                            {isTeamBased ? 'Team / Name' : 'Name'}
                            <SortIcon field="name" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th style={{ width: '20%', ...thStyle }} onClick={() => handleSort('points')}>
                            Total Points
                            <SortIcon field="points" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th style={{ width: '20%', ...thStyle }} onClick={() => handleSort('activities')}>
                            Number of Activities
                            <SortIcon field="activities" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th style={{ width: '30%' }}>Cake Progress</th>
                    </tr>
                </thead>
                {isTeamBased ? (
                    <TeamCompetitorsTableBody
                        competitors={competitors}
                        teams={sortedTeams}
                        pointTarget={pointTarget}
                        onUsernameClick={onUsernameClick}
                    />
                ) : (
                    <CompetitorsTableBody
                        competitors={sortedCompetitors}
                        lowestCompetitor={lowestCompetitor}
                        pointTarget={pointTarget}
                        onUsernameClick={onUsernameClick}
                    />
                )}
            </Table>
        </div>
    );
};

CompetitorsTable.propTypes = {
    competitors: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        totalPoints: PropTypes.number.isRequired,
        numActivities: PropTypes.number.isRequired
    })).isRequired,
    lowestCompetitor: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        profilePicture: PropTypes.string,
        totalPoints: PropTypes.number.isRequired
    }),
    pointTarget: PropTypes.number.isRequired,
    onUsernameClick: PropTypes.func.isRequired,
    isTeamBased: PropTypes.bool,
    teams: PropTypes.array,
};

export default CompetitorsTable;
